/**
 * Column definitions for Audit Decision DataList
 *
 * Defines table columns for displaying audit decisions in a structured format.
 */

import type { GraphQLAuditDecision } from '@internal-audit/domain/graphql-types';
import { Text, Badge, BadgeSize, BadgeVariant } from '@soteria/design-system-ve';

import type { TableColumn } from '../../../../core/components/DataList';
import { formatDecisionTimestamp } from '../statistics/decisionStatistics';

import * as styles from './AuditDecisionColumns.css';
import { LabelControls } from './LabelControls';

// Simple text truncation function
function truncateText(text: string, maxLength: number = 50): string {
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
}

// Helper function to create policy correctness badge
function createPolicyBadge(isCorrect: boolean) {
  return (
    <Badge
      size={BadgeSize.Small}
      text={isCorrect ? 'CORRECT' : 'INCORRECT'}
      title={`Policy decisions are ${isCorrect ? 'correct' : 'incorrect'}`}
      variant={isCorrect ? BadgeVariant.Correct : BadgeVariant.Incorrect}
    />
  );
}

// Helper function to calculate correctness from individual assessments
function calculatePolicyCorrectnessFromAssessments(decision: GraphQLAuditDecision) {
  const triggeredAssessments = decision.triggeredPolicyAssessments || [];
  const nonTriggeredAssessments = decision.nonTriggeredPolicyAssessments || [];
  const allAssessments = [...triggeredAssessments, ...nonTriggeredAssessments];
  const validAssessments = allAssessments.filter(a => a.isCorrect !== null);

  if (validAssessments.length === 0) {
    return <Text>-</Text>;
  }

  const isCorrect = validAssessments.every(a => a.isCorrect === true);

  return createPolicyBadge(isCorrect);
}

// Policy correctness rendering logic
function renderPolicyCorrectness(decision: GraphQLAuditDecision) {
  // For external QA workflow, prioritize the top-level policiesCorrect field
  if (decision.policiesCorrect !== null && decision.policiesCorrect !== undefined) {
    return createPolicyBadge(decision.policiesCorrect);
  }

  // Fallback to calculating from individual assessments for backward compatibility
  return calculatePolicyCorrectnessFromAssessments(decision);
}

/**
 * Creates the column definitions for the audit decision table
 */
export function createAuditDecisionColumns(
  originalClassificationResults?: Array<{
    name: string;
    severity: number;
  }>,
  contentType?: string
): TableColumn<GraphQLAuditDecision>[] {
  const isAudioContent = contentType?.toLowerCase() === 'audio';
  const isImageContent = contentType?.toLowerCase() === 'image';

  const columns: TableColumn<GraphQLAuditDecision>[] = [
    {
      key: 'auditorName',
      header: 'Auditor Name',
      width: '150px',
      render: (decision: GraphQLAuditDecision) => <Text className={styles.auditorName}>{decision.auditorName}</Text>,
    },
    {
      key: 'selectedBy',
      header: 'Selected By',
      width: '150px',
      render: (decision: GraphQLAuditDecision) => (
        <div>
          {decision.selectedBy ? (
            <>
              <Text>{decision.selectedBy}</Text>
              {decision.selectedAt && <Text className={styles.decisionTimestamp}>{formatDecisionTimestamp(decision.selectedAt)}</Text>}
            </>
          ) : (
            <Text>-</Text>
          )}
        </div>
      ),
    },
  ];

  // Only add language and transcription columns for audio content
  if (isAudioContent) {
    columns.push(
      {
        key: 'languageCorrect',
        header: 'Language',
        width: '100px',
        render: (decision: GraphQLAuditDecision) => {
          if (decision.languageCorrect === null || decision.languageCorrect === undefined) {
            return <Text>-</Text>;
          }

          return (
            <Badge
              size={BadgeSize.Small}
              text={decision.languageCorrect ? 'CORRECT' : 'INCORRECT'}
              title={`Language is ${decision.languageCorrect ? 'correct' : 'incorrect'}`}
              variant={decision.languageCorrect ? BadgeVariant.Correct : BadgeVariant.Incorrect}
            />
          );
        },
      },
      {
        key: 'transcriptionCorrect',
        header: 'Transcription',
        width: '120px',
        render: (decision: GraphQLAuditDecision) => {
          if (decision.transcriptionCorrect === null || decision.transcriptionCorrect === undefined) {
            return <Text>-</Text>;
          }

          return (
            <Badge
              size={BadgeSize.Small}
              text={decision.transcriptionCorrect ? 'CORRECT' : 'INCORRECT'}
              title={`Transcription is ${decision.transcriptionCorrect ? 'correct' : 'incorrect'}`}
              variant={decision.transcriptionCorrect ? BadgeVariant.Correct : BadgeVariant.Incorrect}
            />
          );
        },
      },
      {
        key: 'correctedTranscription',
        header: 'Corrected Text',
        width: '200px',
        render: (decision: GraphQLAuditDecision) => (
          <Text title={decision.correctedTranscription || 'No correction provided'}>
            {decision.correctedTranscription ? truncateText(decision.correctedTranscription) : '-'}
          </Text>
        ),
      }
    );
  }

  // Only add OCR columns for image content
  if (isImageContent) {
    columns.push(
      {
        key: 'ocrCorrect',
        header: 'OCR',
        width: '120px',
        render: (decision: GraphQLAuditDecision) => {
          if (decision.ocrCorrect === null || decision.ocrCorrect === undefined) {
            return <Text>-</Text>;
          }

          return (
            <Badge
              size={BadgeSize.Small}
              text={decision.ocrCorrect ? 'CORRECT' : 'INCORRECT'}
              title={`OCR is ${decision.ocrCorrect ? 'correct' : 'incorrect'}`}
              variant={decision.ocrCorrect ? BadgeVariant.Correct : BadgeVariant.Incorrect}
            />
          );
        },
      },
      {
        key: 'correctedOcr',
        header: 'Corrected OCR',
        width: '200px',
        render: (decision: GraphQLAuditDecision) => (
          <Text title={decision.correctedOcr || 'No correction provided'}>{decision.correctedOcr ? truncateText(decision.correctedOcr) : '-'}</Text>
        ),
      }
    );
  }

  // Add remaining columns that apply to all content types
  columns.push(
    {
      key: 'labels',
      header: 'Labels',
      width: '250px',
      render: (decision: GraphQLAuditDecision) => <LabelControls decision={decision} originalClassificationResults={originalClassificationResults} />,
    },
    {
      key: 'policiesCorrect',
      header: 'Policy',
      width: '100px',
      render: renderPolicyCorrectness,
    },
    {
      key: 'comments',
      header: 'Comments',
      width: '200px',
      render: (decision: GraphQLAuditDecision) => (
        <Text title={decision.comments || 'No comments'}>{decision.comments ? truncateText(decision.comments) : '-'}</Text>
      ),
    }
  );

  return columns;
}
